import React from "react";
import { HATCH_STYLE } from "./taskHubUtils";

// Shared presentation pieces for the Reports scoreboard and per-task drill-in,
// so the two levels read as one system.

export const pct = (v) =>
  v === null || v === undefined ? "–" : `${Math.round(v * 100)}%`;

export const BUCKETS = [
  { key: "completed", label: "On time", color: "#059669" },
  { key: "completed_late", label: "Late", color: "#d97706" },
  { key: "missed", label: "Missed", color: "#e11d48" },
];

export function SummaryTiles({ summary }) {
  const finished = (summary?.completed || 0) + (summary?.completed_late || 0);
  const tiles = [
    { label: "Expected", value: summary?.expected ?? 0, sub: "occurrences" },
    {
      label: "On time",
      value: summary?.completed ?? 0,
      sub: pct(summary?.on_time_rate) === "–" ? "of finished" : `${pct(summary?.on_time_rate)} of finished`,
      dot: "#059669",
    },
    { label: "Late", value: summary?.completed_late ?? 0, sub: `${finished} finished total`, dot: "#d97706" },
    { label: "Missed", value: summary?.missed ?? 0, sub: "past due, not done", dot: "#e11d48" },
    {
      label: "Median time",
      value: summary?.median_minutes != null ? `${summary.median_minutes}m` : "–",
      sub: `${summary?.timed_count || 0} timed`,
    },
  ];
  return (
    <div className="grid grid-cols-5 sm:grid-cols-2 gap-3">
      {tiles.map((t) => (
        <div key={t.label} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">
            {t.dot && (
              <span
                className="w-2 h-2 rounded-full border border-transparent"
                style={{ background: t.hatch ? undefined : t.dot, borderColor: t.hatch ? t.dot : "transparent", ...(t.hatch ? HATCH_STYLE : {}) }}
              />
            )}
            {t.label}
          </div>
          <div className="text-2xl font-bold text-slate-900 tabular-nums mt-1.5">
            {t.value}
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">{t.sub}</div>
        </div>
      ))}
    </div>
  );
}

export function StackedTrend({ dailyTrend, title }) {
  const days = dailyTrend || [];
  if (!days.length) return null;
  const labelEvery = Math.max(1, Math.ceil(days.length / 10));
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
      <div className="text-sm font-bold text-slate-900 mb-4">{title}</div>
      <div className="flex items-end gap-1" style={{ height: 120 }}>
        {days.map((d, i) => {
          const expected = d.expected || 0;
          const seg = (n) => (expected ? (n / expected) * 100 : 0);
          const restPct =
            100 - seg(d.completed) - seg(d.completed_late) - seg(d.missed);
          const label = new Date(`${d.date}T12:00:00`).toLocaleDateString(undefined, {
            month: "numeric",
            day: "numeric",
          });
          return (
            <div
              key={d.date}
              className="flex-1 h-full flex flex-col justify-end"
              title={`${d.date}: ${d.expected} expected · ${d.completed} on time · ${d.completed_late} late · ${d.missed} missed`}
            >
              {expected === 0 ? (
                <span className="rounded-t" style={{ height: "2%", background: "#e2e8f0" }} />
              ) : (
                <>
                  {restPct > 0 && <span style={{ height: `${restPct}%`, background: "#f1f5f9" }} className="rounded-t" />}
                  {d.missed > 0 && <span style={{ height: `${seg(d.missed)}%`, background: "#e11d48", opacity: 0.85 }} />}
                  {d.completed_late > 0 && <span style={{ height: `${seg(d.completed_late)}%`, background: "#d97706", opacity: 0.85 }} />}
                  {d.completed > 0 && <span style={{ height: `${seg(d.completed)}%`, background: "#059669", opacity: 0.9 }} />}
                </>
              )}
              <span className="text-[9px] text-slate-400 text-center mt-1 tabular-nums h-3">
                {i % labelEvery === 0 ? label : ""}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex gap-4 flex-wrap mt-3 text-[11px] text-slate-500">
        {BUCKETS.map((b) => (
          <span key={b.key} className="inline-flex items-center gap-1.5">
            <span
              className="w-2.5 h-2.5 rounded"
              style={{ background: b.hatch ? "#e2e8f0" : b.color, ...(b.hatch ? HATCH_STYLE : {}) }}
            />
            {b.label}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded" style={{ background: "#f1f5f9" }} />
          Pending / not started
        </span>
      </div>
    </div>
  );
}

// Proportional outcome bar for one task row in the leaderboard.
export function OutcomeBar({ row }) {
  const expected = row.expected || 0;
  if (!expected) {
    return <span className="block h-2 rounded-full bg-slate-100" />;
  }
  const segs = [
    { n: row.completed, color: "#059669" },
    { n: row.completed_late, color: "#d97706" },
    { n: row.missed, color: "#e11d48" },
    { n: row.pending, color: "#f1f5f9" },
  ];
  return (
    <span className="flex h-2 rounded-full overflow-hidden bg-slate-100">
      {segs.map(
        (s, i) =>
          s.n > 0 && (
            <span
              key={i}
              style={{
                width: `${(s.n / expected) * 100}%`,
                background: s.color,
                ...(s.hatch ? HATCH_STYLE : {}),
              }}
            />
          )
      )}
    </span>
  );
}

export function EmployeeTable({ rows, title }) {
  if (!rows?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 text-sm font-bold text-slate-900 border-b border-slate-100">
        {title}
      </div>
      {rows.slice(0, 8).map((e, i) => (
        <div
          key={`${e.store_id}-${e.employee_id}`}
          className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-b-0"
        >
          <span className="w-5 text-[11px] font-bold text-slate-300 tabular-nums">
            {i + 1}
          </span>
          <span className="flex-1 text-sm font-medium text-slate-800 truncate">
            {e.employee_name || "Employee"}
          </span>
          <span className="text-xs text-slate-500 tabular-nums">
            {e.completed_count} done
          </span>
          <span className="text-xs font-semibold text-emerald-700 tabular-nums w-12 text-right">
            {pct(e.on_time_rate)}
          </span>
        </div>
      ))}
    </div>
  );
}

export function StoreTable({ rows }) {
  if (!rows || rows.length < 2) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-3 text-sm font-bold text-slate-900 border-b border-slate-100">
        By location
      </div>
      {rows.map((b) => (
        <div
          key={b.store_id}
          className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-b-0"
        >
          <span className="flex-1 text-sm font-medium text-slate-800 truncate">
            {b.store_name}
          </span>
          <span className="w-32 sm:hidden">
            <OutcomeBar row={b} />
          </span>
          <span className="text-xs text-slate-500 tabular-nums">
            {b.expected} expected
          </span>
          <span className="text-xs text-slate-500 tabular-nums w-12 text-right" title="Median time on task">
            {b.median_minutes != null ? `${b.median_minutes}m` : "–"}
          </span>
          <span className="text-xs font-semibold text-slate-700 tabular-nums w-12 text-right">
            {pct(b.completion_rate)}
          </span>
        </div>
      ))}
    </div>
  );
}
